import React, { useState, useEffect, useRef } from "react";
import kiraSuperior from "../../../assets/kira-superior_2.png";
import { useUiLang } from "../../../context/UiLangContext";
import type { AppMode, RecordingStatus } from "../hooks/useTranslator";
import {
  Play,
  Square,
  Volume2,
  VolumeX,
  PictureInPicture,
  BookOpen,
  ClipboardList,
  Sun,
  Moon,
  LogOut,
  MoreVertical,
  User,
  Calendar,
  Settings as SettingsIcon,
} from "lucide-react";

type UiLang = "es" | "en";

const UI_LABELS: Record<UiLang, {
  modeTraductor: string;
  modeMinuta: string;
  from: string;
  to: string;
  minutaIn: string;
  start: string;
  stop: string;
  moreOptions: string;
  voiceOn: string;
  voiceOff: string;
  pip: string;
  docs: string;
  minuta: string;
  themeLight: string;
  themeDark: string;
  logout: string;
  uiLangLabel: string;
  modeAlert: string;
  profile: string;
  history: string;
}> = {
  es: {
    modeTraductor: "Traductor + Minuta",
    modeMinuta: "Solo Minuta",
    from: "De",
    to: "A",
    minutaIn: "Minuta en",
    start: "Start",
    stop: "Stop",
    moreOptions: "Más opciones",
    voiceOn: "Voz: Sí",
    voiceOff: "Voz: No",
    pip: "PiP",
    docs: "Docs",
    minuta: "Minuta",
    themeLight: "Tema claro",
    themeDark: "Tema oscuro",
    logout: "Cerrar sesión",
    uiLangLabel: "EN",
    modeAlert: "Por favor, detén la sesión antes de cambiar de modo.",
    profile: "Mi Perfil",
    history: "Historial de Reuniones",
  },
  en: {
    modeTraductor: "Translator + Minutes",
    modeMinuta: "Minutes Only",
    from: "From",
    to: "To",
    minutaIn: "Minutes in",
    start: "Start",
    stop: "Stop",
    moreOptions: "More options",
    voiceOn: "Voice: On",
    voiceOff: "Voice: Off",
    pip: "PiP",
    docs: "Docs",
    minuta: "Minutes",
    themeLight: "Light mode",
    themeDark: "Dark mode",
    logout: "Log out",
    uiLangLabel: "ES",
    modeAlert: "Please stop the session before switching mode.",
    profile: "My Profile",
    history: "Meeting History",
  },
};

interface AppHeaderProps {
  appMode: AppMode;
  recording: boolean;
  voiceEnabled: boolean;
  pipActive: boolean;
  status: RecordingStatus;
  statusText: string;
  srcLang: string;
  tgtLang: string;
  theme: "light" | "dark";
  meetingTranscriptLength: number;
  getStatusColorClass: () => string;
  setAppMode: (mode: AppMode) => void;
  setVoiceEnabled: (v: boolean) => void;
  setSrcLang: (lang: string) => void;
  setTgtLang: (lang: string) => void;
  onTogglePip: () => void;
  onToggleTheme: () => void;
  onOpenDocs: () => void;
  onOpenSummary: () => void;
  onLogout: () => void;
  onOpenProfile: () => void;
  onOpenHistory: () => void;
  onOpenSettings: () => void;
}

export const AppHeader: React.FC<AppHeaderProps> = ({
  appMode,
  recording,
  voiceEnabled,
  pipActive,
  status,
  statusText,
  srcLang,
  tgtLang,
  theme,
  meetingTranscriptLength,
  getStatusColorClass,
  setAppMode,
  setVoiceEnabled,
  setSrcLang,
  setTgtLang,
  onTogglePip,
  onToggleTheme,
  onOpenDocs,
  onOpenSummary,
  onLogout,
  onOpenProfile,
  onOpenHistory,
  onOpenSettings,
}) => {
  const { uiLang, toggleUiLang } = useUiLang();
  const t = UI_LABELS[uiLang];
  const [kebabOpen, setKebabOpen] = useState(false);
  const kebabRef = useRef<HTMLDivElement>(null);

  const handleModeChange = (mode: AppMode) => {
    if (recording) {
      alert(t.modeAlert);
      return;
    }
    setAppMode(mode);
  };

  // Close kebab when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (kebabRef.current && !kebabRef.current.contains(e.target as Node)) {
        setKebabOpen(false);
      }
    };
    if (kebabOpen) {
      document.addEventListener("click", handleClickOutside);
    }
    return () => document.removeEventListener("click", handleClickOutside);
  }, [kebabOpen]);

  const btnBase =
    "inline-flex items-center justify-start text-left gap-[6px] py-[6px] px-[14px] rounded-[6px] border text-[13px] font-medium transition-all duration-150 active:scale-95 cursor-pointer";
  const btnGhost = `${btnBase} border-[var(--border)] bg-[var(--surface)] text-[var(--text-muted)] hover:bg-[var(--surface-hover)] hover:text-[var(--text)]`;

  return (
    <header className="flex items-center justify-between h-[56px] px-5 border-b border-[var(--border)] bg-[var(--sidebar-bg)] backdrop-blur-md z-10 flex-shrink-0 gap-[16px] transition-colors duration-300 relative">
      {/* Left: Brand logo + status */}
      <div className="brand flex items-center gap-3 flex-shrink-0">
        {/* macOS window controls */}
        <div className="traffic-lights flex gap-2 items-center mr-2 flex-shrink-0">
          <span className="w-3 h-3 rounded-full bg-[var(--mac-close)]" title="Cerrar" />
          <span className="w-3 h-3 rounded-full bg-[var(--mac-minimize)]" title="Minimizar" />
          <span className="w-3 h-3 rounded-full bg-[var(--mac-maximize)]" title="Maximizar" />
        </div>
        <img src={kiraSuperior} className="h-[38px] w-auto object-contain bg-transparent flex-shrink-0" alt="Kira" />
        <div
          className={`status-pill inline-flex items-center gap-[6px] font-mono text-[9px] font-medium tracking-[0.02em] uppercase rounded-[100px] py-[3px] px-[9px] ml-3 transition-all duration-300 border before:content-[''] before:w-1.5 before:h-1.5 before:rounded-full ${
            status === "connecting"
              ? "border-yellow-500/30 text-yellow-550 bg-yellow-500/5 before:bg-yellow-500"
              : status === "connected"
              ? "border-emerald-500/30 text-[var(--success)] bg-[rgba(48,209,88,0.05)] before:bg-[var(--success)] before:shadow-[0_0_6px_var(--success)] before:animate-pulse"
              : status === "error"
              ? "border-rose-500/30 text-[var(--danger)] bg-rose-500/5 before:bg-[var(--danger)]"
              : "border-[var(--border)] text-[var(--text-muted)] bg-white/[0.04] before:bg-[var(--text-dim)]"
          }`}
        >
          {statusText}
        </div>
      </div>

      {/* Center: Segmented Control */}
      <div className="header-center flex-1 flex justify-center items-center min-w-0 overflow-hidden">
        <div className="mac-segmented-control flex bg-black/20 border border-[var(--border)] p-[4px] rounded-[8px]">
          <button
            onClick={() => handleModeChange("traductor")}
            className={`text-[13px] font-medium py-[6px] px-[16px] rounded-[6px] cursor-pointer transition-all duration-200 outline-none ${
              appMode === "traductor"
                ? "bg-[var(--accent)] text-white shadow-[0_1px_6px_rgba(0,122,255,0.4)]"
                : "text-[var(--text-muted)] hover:text-[var(--text)]"
            }`}
          >
            {t.modeTraductor}
          </button>
          <button
            onClick={() => handleModeChange("minuta")}
            className={`text-[13px] font-medium py-[6px] px-[16px] rounded-[6px] cursor-pointer transition-all duration-200 outline-none ${
              appMode === "minuta"
                ? "bg-[var(--accent)] text-white shadow-[0_1px_6px_rgba(0,122,255,0.4)]"
                : "text-[var(--text-muted)] hover:text-[var(--text)]"
            }`}
          >
            {t.modeMinuta}
          </button>
        </div>
      </div>

      {/* Right: Controls */}
      <div className="controls flex items-center gap-2">

        {/* Language selectors */}
        <div className="select-wrap flex items-center gap-[6px] bg-[var(--surface)] border border-[var(--border)] rounded-[6px] py-[4px] px-[10px] transition-all duration-200 hover:bg-[var(--surface-hover)]">
          <span className="select-label text-[10px] font-mono font-medium text-[var(--text-dim)] uppercase select-none">{t.from}</span>
          <select
            value={srcLang}
            onChange={(e) => setSrcLang(e.target.value)}
            disabled={recording}
            className="bg-transparent border-none text-[var(--text)] text-[13px] font-medium outline-none cursor-pointer pr-[4px]"
          >
            <option value="auto" className="bg-[var(--window-bg)] text-[var(--text)]">✨ Auto-detectar (Multilingüe)</option>
            <option value="en" className="bg-[var(--window-bg)] text-[var(--text)]">English</option>
            <option value="es" className="bg-[var(--window-bg)] text-[var(--text)]">Español</option>
          </select>
        </div>

        <span className="arrow text-[var(--text-dim)] text-[12px] px-[2px] select-none">→</span>

        <div className="select-wrap flex items-center gap-[6px] bg-[var(--surface)] border border-[var(--border)] rounded-[6px] py-[4px] px-[10px] transition-all duration-200 hover:bg-[var(--surface-hover)]">
          <span className="select-label text-[10px] font-mono font-medium text-[var(--text-dim)] uppercase select-none">
            {appMode === "minuta" ? t.minutaIn : t.to}
          </span>
          <select
            value={tgtLang}
            onChange={(e) => setTgtLang(e.target.value)}
            disabled={recording}
            className="bg-transparent border-none text-[var(--text)] text-[13px] font-medium outline-none cursor-pointer pr-[4px]"
          >
            <option value="es" className="bg-[var(--window-bg)] text-[var(--text)]">Español</option>
            <option value="en" className="bg-[var(--window-bg)] text-[var(--text)]">English</option>
          </select>
        </div>

        {/* AI Summary (Minuta) */}
        <button
          onClick={onOpenSummary}
          disabled={meetingTranscriptLength === 0}
          title={t.minuta}
          className="inline-flex items-center justify-center gap-[6px] py-[6px] px-[14px] rounded-[6px] border border-[var(--border)] bg-[var(--surface)] text-[var(--text-muted)] hover:bg-[var(--surface-hover)] hover:text-[var(--text)] text-[13px] font-medium transition-all duration-150 active:scale-95 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ClipboardList size={14} />
          <span>{t.minuta}</span>
        </button>

        {/* ─── Kebab Menu ─── */}
        <div className="relative inline-flex items-center" ref={kebabRef}>
          {/* Trigger */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setKebabOpen((v) => !v);
            }}
            title={t.moreOptions}
            className={`inline-flex items-center justify-center py-[6px] px-[10px] rounded-[6px] border text-[16px] font-bold transition-all duration-150 active:scale-95 cursor-pointer ${
              kebabOpen
                ? "border-[var(--accent)]/40 bg-[var(--accent-muted)] text-[var(--accent)]"
                : "border-[var(--border)] bg-transparent text-[var(--text-muted)] hover:bg-white/10 hover:text-[var(--text)]"
            }`}
          >
            <MoreVertical size={16} />
          </button>

          {/* Dropdown */}
          {kebabOpen && (
            <div
              className="absolute right-0 top-[calc(100%+8px)] z-[100] min-w-[188px] flex flex-col gap-[6px] p-[10px] rounded-[8px] bg-[var(--surface)] border border-[var(--border)] shadow-[0_4px_16px_rgba(0,0,0,0.4)]"
              style={{ animation: "modal-appear 0.18s cubic-bezier(0.16,1,0.3,1)" }}
            >
              {/* Voice */}
              {appMode === "traductor" && (
                <button
                  onClick={() => { setVoiceEnabled(!voiceEnabled); setKebabOpen(false); }}
                  title="Activar/Desactivar lectura de voz"
                  className={`w-full justify-start ${btnBase} ${
                    voiceEnabled
                      ? "bg-[rgba(48,209,88,0.22)] border-[rgba(48,209,88,0.4)] text-[var(--success)] shadow-[0_0_8px_rgba(48,209,88,0.2)]"
                      : "bg-[rgba(48,209,88,0.1)] border-[rgba(48,209,88,0.25)] text-[var(--success)] hover:bg-[rgba(48,209,88,0.18)]"
                  }`}
                >
                  {voiceEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
                  <span>{voiceEnabled ? t.voiceOn : t.voiceOff}</span>
                </button>
              )}

              {/* PiP */}
              {appMode === "traductor" && (
                <button
                  onClick={() => { onTogglePip(); setKebabOpen(false); }}
                  title="Ventana PiP flotante"
                  className={`w-full justify-start ${btnBase} ${
                    pipActive
                      ? "bg-[var(--accent-muted)] border-[var(--accent)]/40 text-[var(--accent)] shadow-[0_0_8px_rgba(0,122,255,0.2)]"
                      : btnGhost
                  }`}
                >
                  <PictureInPicture size={14} />
                  <span>{t.pip}</span>
                </button>
              )}

              {/* Docs */}
              <button
                onClick={() => { onOpenDocs(); setKebabOpen(false); }}
                title="Documentación"
                className={`w-full justify-start ${btnGhost}`}
              >
                <BookOpen size={14} />
                <span>{t.docs}</span>
              </button>



              {/* Profile option */}
              <button
                onClick={() => { onOpenProfile(); setKebabOpen(false); }}
                title={t.profile}
                className={`w-full justify-start ${btnGhost}`}
              >
                <User size={14} />
                <span>{t.profile}</span>
              </button>

              {/* History option */}
              <button
                onClick={() => { onOpenHistory(); setKebabOpen(false); }}
                title={t.history}
                className={`w-full justify-start ${btnGhost}`}
              >
                <Calendar size={14} />
                <span>{t.history}</span>
              </button>

              {/* Settings option */}
              <button
                onClick={() => { onOpenSettings(); setKebabOpen(false); }}
                title={uiLang === "en" ? "Settings" : "Configuración"}
                className={`w-full justify-start ${btnGhost}`}
              >
                <SettingsIcon size={14} />
                <span>{uiLang === "en" ? "Settings" : "Configuración"}</span>
              </button>

              {/* Separator */}
              <div className="border-t border-[var(--border)] my-1" />

              {/* Theme */}
              <button
                onClick={() => { onToggleTheme(); setKebabOpen(false); }}
                title="Cambiar tema visual"
                className={`w-full justify-start ${btnGhost}`}
              >
                {theme === "dark" ? <Sun size={14} /> : <Moon size={14} />}
                <span>{theme === "dark" ? t.themeLight : t.themeDark}</span>
              </button>

              {/* UI Language toggle */}
              <button
                onClick={() => { toggleUiLang(); }}
                title="Cambiar idioma de la interfaz"
                className={`w-full justify-start ${btnBase} border-[var(--accent)]/30 bg-[var(--accent-muted)] text-[var(--accent)] hover:bg-[var(--accent-muted)] font-mono tracking-wide`}
              >
                <span className="text-[13px]">🌐</span>
                <span>{t.uiLangLabel}</span>
              </button>

              {/* Logout */}
              <button
                onClick={() => { onLogout(); setKebabOpen(false); }}
                title={t.logout}
                className={`w-full justify-start ${btnBase} border-[var(--border)] bg-[var(--surface)] text-rose-400/70 hover:bg-[var(--surface-hover)] hover:text-rose-400`}
              >
                <LogOut size={14} />
                <span>{t.logout}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
